import { useEffect, useState } from "react";

import { Box, Button, Stack, TextField } from "@mui/material";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid, GridColDef } from "@mui/x-data-grid";

import { HttpMainApi } from "../../../interface/main-api";
import { userState } from "../../../interface/MainInterface";
import LoadingCircle from "../../../utils/LoadingCircle";

interface AdminReservationUserSearchProps {
  userState: userState;
  selectLevel?: string;
  selectButtonLabel?: string;
  toolbarPlacement?: "absolute" | "static";
  onSelectUser?: (user: userState) => void;
  onSearchSuccess?: () => void;
  renderActionColumns?: () => GridColDef[];
}

const mainApi = new HttpMainApi();

const baseColumns: GridColDef[] = [
  {
    field: "userId",
    headerName: "회원 아이디",
    width: 110,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "userName",
    headerName: "회원 이름",
    width: 150,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "readAddress",
    headerName: "동/호",
    width: 150,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "readInputDt",
    headerName: "가입 신청일",
    type: "number",
    width: 200,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "userLevel",
    headerName: "권한",
    width: 110,
    editable: false,
    align: "center",
    headerAlign: "center",
  },
];

const AdminReservationUserSearch = ({
  selectLevel = "회원",
  selectButtonLabel = "선택",
  toolbarPlacement = "absolute",
  onSelectUser,
  onSearchSuccess,
  renderActionColumns,
}: AdminReservationUserSearchProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [userList, setUserList] = useState<userState[]>([]);
  const [userName, setUserName] = useState("");

  useEffect(() => {
    return () => {
      setIsLoading(false);
      setUserList([]);
    };
  }, []);

  const get_user_list = async () => {
    if (userName === "") {
      alert("회원 이름을 넣어주세요");
      return;
    }
    setIsLoading(true);
    setUserList([]);
    const param = {
      pk: "COMMUNITY/USER",
      sk: userName,
      selectLevel,
    };

    const res = await mainApi.get_user_list(param);
    if (res.code === "200") {
      setUserList(res.response.result);
      onSearchSuccess?.();
    } else if (res.code === "400") {
    } else {
      alert(
        `알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`
      );
    }
    setIsLoading(false);
  };

  const selectColumn: GridColDef | null = onSelectUser
    ? {
        field: "selectUser",
        headerName: selectButtonLabel,
        width: 110,
        editable: false,
        renderCell: (params) => (
          <Button color="primary" onClick={() => onSelectUser(params.row)}>
            <AddBoxIcon />
          </Button>
        ),
        align: "center",
        headerAlign: "center",
      }
    : null;

  const columns = [
    ...baseColumns,
    ...(renderActionColumns ? renderActionColumns() : []),
    ...(selectColumn ? [selectColumn] : []),
  ];

  return (
    <>
      <Box
        sx={
          toolbarPlacement === "absolute"
            ? { position: "absolute", top: "60px", right: "20px" }
            : { mb: 2, display: "flex", justifyContent: "flex-end" }
        }
      >
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <TextField
            id="userId"
            value={userName}
            placeholder="이름"
            variant="outlined"
            label="이름"
            autoFocus
            fullWidth
            type="text"
            autoComplete="off"
            inputProps={{
              enterKeyHint: "Enter",
              style: { padding: "12px 16px 10px 16px" },
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                get_user_list();
              }
            }}
            onChange={(e) => {
              setUserName(e.target.value);
            }}
            sx={{ marginTop: "9px !important" }}
          />
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={() => {
              get_user_list();
            }}
            sx={{
              borderRadius: "4px",
              width: "54px",
              height: "54px",
              marginTop: "9px !important",
            }}
          >
            <SearchIcon />
          </Button>
        </Stack>
      </Box>
      <DataGrid
        rows={userList}
        columns={columns}
        disableRowSelectionOnClick
        style={{ height: 250 }}
        getRowId={(row) => (row.userId ? row.userId.toString() : "")}
      />
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default AdminReservationUserSearch;
